import { Message } from '../../types/lesson';
import MathRenderer from '../common/MathRenderer';
import DisplayContent from '../common/DisplayContent';

interface ChatMessageProps {
  message: Message;
  isCurrentUser: boolean;
  isLessonHistory?: boolean;
}

export default function ChatMessage({ message, isCurrentUser, isLessonHistory = false }: ChatMessageProps) {
  return (
    <div className={`flex items-start gap-3 p-4 ${isCurrentUser ? 'justify-end' : ''}`}>
      {!isCurrentUser && (
        <div
          className="bg-center bg-no-repeat aspect-square bg-cover rounded-full w-10 shrink-0"
          style={
            message.sender.avatar
              ? { backgroundImage: `url(${message.sender.avatar})` }
              : undefined
          }
        >
          {!message.sender.avatar && (
            <div className="bg-primary rounded-full w-10 h-10 flex items-center justify-center text-white text-lg font-bold">
              {message.sender.name.charAt(0)}
            </div>
          )}
        </div>
      )}
      
      <div className={`flex flex-1 flex-col gap-2 ${isCurrentUser ? 'items-end' : 'items-start'}`}>
        <p className="text-text-secondary text-[13px] font-normal leading-normal max-w-[360px] text-right">
          {message.sender.name}
        </p>
        
        <div className={`flex flex-col gap-2 ${isCurrentUser ? 'max-w-[360px]' : 'max-w-[100%]'}`}>
          {/* Display image if present */}
          {message.image && (
            <div className="rounded-lg overflow-hidden border border-gray-600">
              <img
                src={message.image.preview}
                alt="Uploaded content"
                className="w-full max-w-[300px] h-auto object-cover"
              />
            </div>
          )}
          
          {/* Display text content */}
          {message.content && typeof message.content === 'string' && (
            /* Show text content if:
               1. It's lesson history (always show text), OR
               2. There's no structured display content or display arrays are empty */
            (isLessonHistory || ((!message.displayItems || message.displayItems.length === 0) && !message.displayContent)) && (
              <div
                className={`text-base font-normal leading-normal rounded px-4 py-3 ${
                  isCurrentUser
                    ? 'bg-chalk-blue/20 border border-chalk-blue/30'
                    : 'bg-black/30 border border-gray-500/50'
                } text-chalk-white`}
              >
                {/* Only render LaTeX for AI messages, not user messages */}
                {isCurrentUser ? (
                  <div className="whitespace-pre-wrap">{message.content}</div>
                ) : (
                  <MathRenderer content={message.content} />
                )}
              </div>
            )
          )}
          
          {/* Display structured content if present */}
          {message.displayItems && message.displayItems.length > 0 && (
            <div className={`rounded px-4 py-3 ${
              isCurrentUser 
                ? 'bg-chalk-blue/20 border border-chalk-blue/30' 
                : 'bg-black/30 border border-gray-500/50'
            } text-chalk-white`}>
              <DisplayContent displayItems={message.displayItems} />
            </div>
          )}
          
          {/* Display legacy display content if present */}
          {message.displayContent && !message.displayItems && (
            <div className={`rounded px-4 py-3 ${
              isCurrentUser
                ? 'bg-chalk-blue/20 border border-chalk-blue/30'
                : 'bg-black/30 border border-gray-500/50'
            } text-chalk-white`}>
              <div className="content-item">
                {message.displayContent.content}
              </div>
            </div>
          )}
        </div>
      </div>

      {isCurrentUser && (
        <div
          className="bg-center bg-no-repeat aspect-square bg-cover rounded-full w-10 shrink-0"
          style={
            message.sender.avatar
              ? { backgroundImage: `url(${message.sender.avatar})` }
              : undefined
          }
        >
          {!message.sender.avatar && (
            <div className="bg-primary rounded-full w-10 h-10 flex items-center justify-center text-white text-lg font-bold">
              {message.sender.name.charAt(0)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
